import type {
  BrandConfig,
  CoachFile,
  GameLocation,
  LocationsFile,
  PhysicsConfig,
  StringsFile,
  TrackDef,
  Tyre,
  TyreCatalogue,
} from './types';

/** Validation failure with a path that pinpoints the offending field. */
export class ValidationError extends Error {
  constructor(public path: string, message: string) {
    super(`${path}: ${message}`);
    this.name = 'ValidationError';
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown, path: string, min: number, max: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new ValidationError(path, `expected a number, got ${typeof v}`);
  }
  if (v < min || v > max) {
    throw new ValidationError(path, `expected ${min}..${max}, got ${v}`);
  }
  return v;
}

function str(v: unknown, path: string): string {
  if (typeof v !== 'string' || v.length === 0) {
    throw new ValidationError(path, 'expected a non-empty string');
  }
  return v;
}

const DISCIPLINES = new Set([
  'road',
  'mtb',
  'bmx',
  'moto-street',
  'moto-dirt',
  'moto-commuter',
]);

function validateTyre(v: unknown, path: string): Tyre {
  if (!isRecord(v)) throw new ValidationError(path, 'expected an object');
  const id = str(v.id, `${path}.id`);
  const name = str(v.name, `${path}.name`);
  const tagline = str(v.tagline, `${path}.tagline`);
  if (typeof v.placeholder !== 'boolean') {
    throw new ValidationError(`${path}.placeholder`, 'expected a boolean');
  }
  if (!Array.isArray(v.disciplines) || v.disciplines.length === 0) {
    throw new ValidationError(`${path}.disciplines`, 'expected a non-empty array');
  }
  for (const d of v.disciplines) {
    if (typeof d !== 'string' || !DISCIPLINES.has(d)) {
      throw new ValidationError(`${path}.disciplines`, `unknown discipline "${d}"`);
    }
  }
  const sp = v.specs;
  if (!isRecord(sp)) throw new ValidationError(`${path}.specs`, 'expected an object');
  const specs: Tyre['specs'] = {
    compound: num(sp.compound, `${path}.specs.compound`, 0, 1),
    tpi: num(sp.tpi, `${path}.specs.tpi`, 20, 200),
    tread: num(sp.tread, `${path}.specs.tread`, 0, 1),
    width: num(sp.width, `${path}.specs.width`, 18, 260),
    bead: (() => {
      if (sp.bead !== 'wire' && sp.bead !== 'folding') {
        throw new ValidationError(`${path}.specs.bead`, 'expected "wire" or "folding"');
      }
      return sp.bead;
    })(),
  };
  if (sp.tempWindow !== undefined) {
    if (!isRecord(sp.tempWindow)) {
      throw new ValidationError(`${path}.specs.tempWindow`, 'expected an object');
    }
    specs.tempWindow = {
      min: num(sp.tempWindow.min, `${path}.specs.tempWindow.min`, 0, 200),
      max: num(sp.tempWindow.max, `${path}.specs.tempWindow.max`, 0, 250),
    };
    if (specs.tempWindow.max <= specs.tempWindow.min) {
      throw new ValidationError(`${path}.specs.tempWindow`, 'max must exceed min');
    }
  }
  // Temperature is the Moto Street educational star (PROMPT §2): every
  // street-capable tyre needs a window; dirt-only knobbies don't simulate it.
  const isStreetMoto = (v.disciplines as string[]).some(
    (d) => d === 'moto-street' || d === 'moto-commuter',
  );
  if (isStreetMoto && !specs.tempWindow) {
    throw new ValidationError(
      `${path}.specs.tempWindow`,
      'required for moto-street/commuter tyres',
    );
  }
  const st = v.stats;
  if (!isRecord(st)) throw new ValidationError(`${path}.stats`, 'expected an object');
  const stats: Tyre['stats'] = {
    grip: num(st.grip, `${path}.stats.grip`, 0, 100),
    wetLoose: num(st.wetLoose, `${path}.stats.wetLoose`, 0, 100),
    speed: num(st.speed, `${path}.stats.speed`, 0, 100),
    cornering: num(st.cornering, `${path}.stats.cornering`, 0, 100),
    toughness: num(st.toughness, `${path}.stats.toughness`, 0, 100),
    comfort: num(st.comfort, `${path}.stats.comfort`, 0, 100),
  };
  let product: Tyre['product'];
  if (v.product !== undefined) {
    const p = v.product;
    if (!isRecord(p) || !Array.isArray(p.markets) || !p.markets.length || !p.markets.every(c => typeof c === 'string' && /^[A-Z]{2}$/.test(c) && c !== 'IN') || !Array.isArray(p.pricingEvidence) || !p.pricingEvidence.length) throw new ValidationError(`${path}.product`, 'expected international product provenance');
    const productId = num(p.productId, `${path}.product.productId`, 1, 10000000);
    const date = (value: unknown) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    if (!date(p.pricingAsOf) || !p.pricingEvidence.every(r => isRecord(r) && Number.isInteger(r.id) && r.productId === productId && typeof r.country === 'string' && (p.markets as string[]).includes(r.country) && typeof r.currency === 'string' && /^[A-Z]{3}$/.test(r.currency) && r.currency !== 'INR' && date(r.startDate) && date(r.endDate) && String(r.startDate) <= String(p.pricingAsOf) && String(r.endDate) >= String(p.pricingAsOf))) throw new ValidationError(`${path}.product.pricingEvidence`, 'expected valid non-India pricing at export date');
    if (p.image !== undefined && (typeof p.image !== 'string' || !/^products\/[a-zA-Z0-9_-]+\.(jpg|png|webp)$/.test(p.image))) throw new ValidationError(`${path}.product.image`, 'expected a local product image');
    product = { productId, style: str(p.style, path), catalogueName: str(p.catalogueName, path), category: str(p.category, path), etrto: str(p.etrto, path), tpi: num(p.tpi, path, 20, 200), weightGrams: p.weightGrams === null ? null : num(p.weightGrams, path, 1, 10000), markets: p.markets as string[], pricingAsOf: p.pricingAsOf as string, pricingEvidence: p.pricingEvidence as NonNullable<Tyre['product']>['pricingEvidence'], source: str(p.source, path), ...(p.image ? { image: p.image as string, imageProductId: num(p.imageProductId, path, 1, 10000000), imageNote: str(p.imageNote, path) } : {}) };
  }
  return {
    id,
    name,
    tagline,
    placeholder: v.placeholder,
    disciplines: v.disciplines as Tyre['disciplines'],
    specs,
    stats,
    ...(product ? { product } : {}),
  };
}

export function validateTyreCatalogue(v: unknown): TyreCatalogue {
  if (!isRecord(v)) throw new ValidationError('$', 'expected an object');
  const schemaVersion = num(v.schemaVersion, '$.schemaVersion', 1, 1);
  if (!Array.isArray(v.tyres) || v.tyres.length === 0) {
    throw new ValidationError('$.tyres', 'expected a non-empty array');
  }
  const tyres = v.tyres.map((t, i) => validateTyre(t, `$.tyres[${i}]`));
  const ids = new Set<string>();
  for (const t of tyres) {
    if (ids.has(t.id)) throw new ValidationError('$.tyres', `duplicate id "${t.id}"`);
    ids.add(t.id);
  }
  return { schemaVersion, tyres };
}

export function validateBrand(v: unknown): BrandConfig {
  if (!isRecord(v)) throw new ValidationError('$', 'expected an object');
  const colors = v.colors;
  if (!isRecord(colors)) throw new ValidationError('$.colors', 'expected an object');
  if (
    v.apiBaseUrl !== undefined &&
    v.apiBaseUrl !== null &&
    typeof v.apiBaseUrl !== 'string'
  ) {
    throw new ValidationError('$.apiBaseUrl', 'expected a string or null');
  }
  return {
    name: str(v.name, '$.name'),
    logo: str(v.logo, '$.logo'),
    logoMark: str(v.logoMark, '$.logoMark'),
    colors: {
      primary: str(colors.primary, '$.colors.primary'),
      accent: str(colors.accent, '$.colors.accent'),
      dark: str(colors.dark, '$.colors.dark'),
    },
    apiBaseUrl: (v.apiBaseUrl as string | null | undefined) ?? null,
  };
}

function validateTrack(v: unknown, path: string): TrackDef {
  if (!isRecord(v)) throw new ValidationError(path, 'expected an object');
  const discipline = str(v.discipline, `${path}.discipline`);
  if (!DISCIPLINES.has(discipline)) {
    throw new ValidationError(`${path}.discipline`, `unknown discipline "${discipline}"`);
  }
  if (!isRecord(v.ground)) {
    throw new ValidationError(`${path}.ground`, 'expected an object');
  }
  if (!Array.isArray(v.points) || v.points.length < 4) {
    throw new ValidationError(`${path}.points`, 'expected an array of ≥4 control points');
  }
  const points = v.points.map((p, i) => {
    const pp = `${path}.points[${i}]`;
    if (!isRecord(p)) throw new ValidationError(pp, 'expected an object');
    return {
      x: num(p.x, `${pp}.x`, -2000, 2000),
      y: num(p.y, `${pp}.y`, -200, 200),
      z: num(p.z, `${pp}.z`, -2000, 2000),
      surface: str(p.surface, `${pp}.surface`),
    };
  });
  let medals: TrackDef['medals'];
  if (v.medals !== undefined) {
    if (!isRecord(v.medals)) {
      throw new ValidationError(`${path}.medals`, 'expected an object');
    }
    medals = {
      gold: num(v.medals.gold, `${path}.medals.gold`, 1, 3600),
      silver: num(v.medals.silver, `${path}.medals.silver`, 1, 3600),
      bronze: num(v.medals.bronze, `${path}.medals.bronze`, 1, 3600),
    };
    if (!(medals.gold < medals.silver && medals.silver < medals.bronze)) {
      throw new ValidationError(
        `${path}.medals`,
        'expected gold < silver < bronze (seconds)',
      );
    }
  }
  return {
    id: str(v.id, `${path}.id`),
    name: str(v.name, `${path}.name`),
    discipline: discipline as TrackDef['discipline'],
    width: num(v.width, `${path}.width`, 3, 30),
    ground: {
      surface: str(v.ground.surface, `${path}.ground.surface`),
      y: num(v.ground.y, `${path}.ground.y`, -50, 50),
    },
    points,
    ...(medals ? { medals } : {}),
  };
}

export function validateLocations(v: unknown): LocationsFile {
  if (!isRecord(v)) throw new ValidationError('$', 'expected an object');
  const schemaVersion = num(v.schemaVersion, '$.schemaVersion', 1, 1);
  if (!Array.isArray(v.locations) || v.locations.length === 0) {
    throw new ValidationError('$.locations', 'expected a non-empty array');
  }
  const locations: GameLocation[] = v.locations.map((loc, i) => {
    const path = `$.locations[${i}]`;
    if (!isRecord(loc)) throw new ValidationError(path, 'expected an object');
    if (!Array.isArray(loc.tracks) || loc.tracks.length === 0) {
      throw new ValidationError(`${path}.tracks`, 'expected a non-empty array');
    }
    return {
      id: str(loc.id, `${path}.id`),
      name: str(loc.name, `${path}.name`),
      biome: str(loc.biome, `${path}.biome`),
      tracks: loc.tracks.map((t, j) => validateTrack(t, `${path}.tracks[${j}]`)),
    };
  });
  // Track ids are used as URL params and save keys: globally unique.
  const ids = new Set<string>();
  for (const loc of locations) {
    if (ids.has(loc.id)) throw new ValidationError('$.locations', `duplicate id "${loc.id}"`);
    ids.add(loc.id);
    for (const t of loc.tracks) {
      if (ids.has(t.id)) throw new ValidationError('$.locations', `duplicate id "${t.id}"`);
      ids.add(t.id);
    }
  }
  return { schemaVersion, locations };
}

export function validateCoach(v: unknown): CoachFile {
  if (!isRecord(v)) throw new ValidationError('$', 'expected an object');
  const schemaVersion = num(v.schemaVersion, '$.schemaVersion', 1, 1);
  const locale = str(v.locale, '$.locale');
  if (!isRecord(v.triggers)) {
    throw new ValidationError('$.triggers', 'expected an object');
  }
  const triggers: CoachFile['triggers'] = {};
  for (const [name, lines] of Object.entries(v.triggers)) {
    const path = `$.triggers.${name}`;
    if (!Array.isArray(lines) || lines.length === 0) {
      throw new ValidationError(path, 'expected a non-empty array');
    }
    triggers[name] = lines.map((l, i) => {
      const lp = `${path}[${i}]`;
      if (!isRecord(l)) throw new ValidationError(lp, 'expected an object');
      const tier = num(l.tier, `${lp}.tier`, 0, 2);
      if (!Number.isInteger(tier)) {
        throw new ValidationError(`${lp}.tier`, 'expected an integer');
      }
      if (l.plug !== undefined && typeof l.plug !== 'boolean') {
        throw new ValidationError(`${lp}.plug`, 'expected a boolean');
      }
      return {
        tier,
        text: str(l.text, `${lp}.text`),
        ...(l.plug ? { plug: true } : {}),
      };
    });
  }
  if (Object.keys(triggers).length === 0) {
    throw new ValidationError('$.triggers', 'expected at least one trigger');
  }
  if (v.machineDraft !== undefined && typeof v.machineDraft !== 'boolean') {
    throw new ValidationError('$.machineDraft', 'expected a boolean');
  }
  return {
    schemaVersion,
    locale,
    ...(v.machineDraft !== undefined ? { machineDraft: v.machineDraft } : {}),
    triggers,
  };
}

export function validateStrings(v: unknown): StringsFile {
  if (!isRecord(v)) throw new ValidationError('$', 'expected an object');
  const schemaVersion = num(v.schemaVersion, '$.schemaVersion', 1, 1);
  const locale = str(v.locale, '$.locale');
  if (typeof v.machineDraft !== 'boolean') {
    throw new ValidationError('$.machineDraft', 'expected a boolean');
  }
  if (!isRecord(v.strings)) {
    throw new ValidationError('$.strings', 'expected an object');
  }
  const strings: StringsFile['strings'] = {};
  for (const [key, text] of Object.entries(v.strings)) {
    strings[key] = str(text, `$.strings.${key}`);
  }
  if (Object.keys(strings).length === 0) {
    throw new ValidationError('$.strings', 'expected at least one string');
  }
  return { schemaVersion, locale, machineDraft: v.machineDraft, strings };
}

export function validatePhysics(v: unknown): PhysicsConfig {
  if (!isRecord(v)) throw new ValidationError('$', 'expected an object');
  const schemaVersion = num(v.schemaVersion, '$.schemaVersion', 1, 1);
  const susp = v.suspension;
  if (!isRecord(susp)) throw new ValidationError('$.suspension', 'expected an object');
  if (!isRecord(v.surfaces)) throw new ValidationError('$.surfaces', 'expected an object');
  const surfaces: PhysicsConfig['surfaces'] = {};
  for (const [name, s] of Object.entries(v.surfaces)) {
    if (!isRecord(s)) throw new ValidationError(`$.surfaces.${name}`, 'expected an object');
    surfaces[name] = {
      muBase: num(s.muBase, `$.surfaces.${name}.muBase`, 0, 2),
      w: num(s.w, `$.surfaces.${name}.w`, 0, 1),
      r: num(s.r, `$.surfaces.${name}.r`, 0, 1),
      h: num(s.h, `$.surfaces.${name}.h`, 0, 1),
      crrMult: num(s.crrMult, `$.surfaces.${name}.crrMult`, 0, 10),
      color: str(s.color, `$.surfaces.${name}.color`),
    };
  }
  if (Object.keys(surfaces).length === 0) {
    throw new ValidationError('$.surfaces', 'expected at least one surface');
  }
  return {
    schemaVersion,
    gravity: num(v.gravity, '$.gravity', 1, 30),
    fixedDt: num(v.fixedDt, '$.fixedDt', 1 / 240, 1 / 30),
    suspension: {
      restLen: num(susp.restLen, '$.suspension.restLen', 0.1, 1),
      sag: num(susp.sag, '$.suspension.sag', 0.05, 0.6),
      dampingRatio: num(susp.dampingRatio, '$.suspension.dampingRatio', 0.05, 2),
    },
    surfaces,
  };
}
